import { useState } from 'react';

import type { FleetDevice } from 'src/api/endpoints/cloudEnterprise.types';
import { useAccessTokenClaims } from 'src/api/queries/user';
import { Divider, Modal } from 'src/components';
import {
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuLabel,
} from 'src/components/DropdownMenu';
import { userHelpers } from 'src/utils/userHelpers';

import { DropdownMenuGroup } from '@radix-ui/react-dropdown-menu';
import { IS_DEVELOPMENT } from 'src/config';
import { useFeatureFlags } from 'src/utils/useFeatureFlags';
import { DeviceActionSoftwareAutoInstall } from './DeviceActionSoftwareAutoInstall';
import { DeviceActionSoftwareForceRestart } from './DeviceActionSoftwareForceRestart';
import { DeviceActionTriggerRemoteWipe } from './DeviceActionTriggerRemoteWipe';
import { DeviceActionSetAutolockTimeout } from './DeviceActionTriggerSetAutolockTimeout';
import { DeviceActionTriggerSetPasscodeLength } from './DeviceActionTriggerSetPasscodeLength';
import { DeviceActionTriggerUnpair } from './DeviceActionTriggerUnpair';

type ModalState =
  | 'REMOTE_WIPE'
  | 'PASSCODE_LENGTH'
  | 'AUTOLOCK_TIMEOUT'
  | 'SOFTWARE_AUTO_INSTALL'
  | 'SOFTWARE_FORCE_RESTART'
  | 'UNPAIR';

export function DeviceMenu({
  device,
  userId,
  userEmail,
  children,
}: {
  device: FleetDevice;
  userId: string;
  userEmail: string | undefined;
  children?: React.ReactNode;
}) {
  const featureFlags = useFeatureFlags();
  const accessTokenClaims = useAccessTokenClaims();
  const [open, setOpen] = useState<boolean>(false);
  const [modalVariant, setModalVariant] = useState<ModalState>();

  if (!userHelpers.hasPermissionCreateCommands(accessTokenClaims.data)) {
    return null;
  }

  return (
    <Modal.Root open={open} onOpenChange={setOpen}>
      <DropdownMenu
        trigger={children}
        onClick={(e) => {
          // Needed to prevent parent click handlers from being triggered
          e.stopPropagation();
        }}
      >
        <Modal.Trigger
          asChild
          onClick={() => {
            setModalVariant('REMOTE_WIPE');
          }}
        >
          <DropdownMenuItem>Remote wipe</DropdownMenuItem>
        </Modal.Trigger>

        {featureFlags.EnableEnterpriseForceUnpairDevice && (
          <Modal.Trigger
            asChild
            onClick={() => {
              setModalVariant('UNPAIR');
            }}
          >
            <DropdownMenuItem>Force unpair device</DropdownMenuItem>
          </Modal.Trigger>
        )}

        {(IS_DEVELOPMENT || featureFlags.EnableEnterpriseCommands) && (
          <DropdownMenuGroup>
            <Divider className="mx-12 mb-8 opacity-50" />
            <DropdownMenuLabel>Development</DropdownMenuLabel>
            <Modal.Trigger
              asChild
              onClick={() => {
                setModalVariant('PASSCODE_LENGTH');
              }}
            >
              <DropdownMenuItem>Set passcode length</DropdownMenuItem>
            </Modal.Trigger>

            <Modal.Trigger
              asChild
              onClick={() => {
                setModalVariant('AUTOLOCK_TIMEOUT');
              }}
            >
              <DropdownMenuItem>Set autolock time</DropdownMenuItem>
            </Modal.Trigger>

            <Modal.Trigger
              asChild
              onClick={() => {
                setModalVariant('SOFTWARE_AUTO_INSTALL');
              }}
            >
              <DropdownMenuItem>Software auto install</DropdownMenuItem>
            </Modal.Trigger>

            <Modal.Trigger
              asChild
              onClick={() => {
                setModalVariant('SOFTWARE_FORCE_RESTART');
              }}
            >
              <DropdownMenuItem>Software force restart</DropdownMenuItem>
            </Modal.Trigger>
          </DropdownMenuGroup>
        )}
      </DropdownMenu>

      {modalVariant === 'REMOTE_WIPE' && (
        <DeviceActionTriggerRemoteWipe
          device={device}
          userEmail={userEmail}
          userId={userId}
          onSuccess={() => {
            setOpen(false);
          }}
        />
      )}
      {modalVariant === 'PASSCODE_LENGTH' && (
        <DeviceActionTriggerSetPasscodeLength
          device={device}
          userId={userId}
          onSuccess={() => {
            setOpen(false);
          }}
        />
      )}
      {modalVariant === 'AUTOLOCK_TIMEOUT' && (
        <DeviceActionSetAutolockTimeout
          device={device}
          userId={userId}
          onSuccess={() => {
            setOpen(false);
          }}
        />
      )}
      {modalVariant === 'UNPAIR' && (
        <DeviceActionTriggerUnpair
          device={device}
          userId={userId}
          userEmail={userEmail}
          onSuccess={() => {
            setOpen(false);
          }}
        />
      )}
      {modalVariant === 'SOFTWARE_AUTO_INSTALL' && (
        <DeviceActionSoftwareAutoInstall
          device={device}
          userId={userId}
          userEmail={userEmail}
          onSuccess={() => {
            setOpen(false);
          }}
        />
      )}
      {modalVariant === 'SOFTWARE_FORCE_RESTART' && (
        <DeviceActionSoftwareForceRestart
          device={device}
          userId={userId}
          userEmail={userEmail}
          onSuccess={() => {
            setOpen(false);
          }}
        />
      )}
    </Modal.Root>
  );
}
